import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./AccountSubscriptionCard.module.scss";

import Modal from "../modal/modal";
import GetPremiumModal from "../modals/GetPremiumModal/GetPremiumModal";
import OrderSummaryModal from "../modals/OrderSummaryModal/OrderSummaryModal";
import RenewalModal from "../modals/RenewalModal/RenewalModal";
import ProcessingModal from "../modals/ProcessingModal/ProcessingModal";
import SuccessfullyRenewalModal from "../modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";
import { ILicenses, subscriptionData } from "../../types/details-api-types";
import { useToast } from "../../hooks/useToast";
import LicenseKeysModal from "../modals/LicenseKeysModal/LicenseKeysModal";

// Images
import device from "../../assets/icons/devices.svg";
import devicePause from "../../assets/icons/device-pause.svg";
import deviceCancel from "../../assets/icons/device-cancel.svg";
import download from "../../assets/icons/download.svg";
import install from "../../assets/icons/install.svg";
import { trackEvent } from "../../utils/analytics";
import { EVENT_NAMES } from "../../types/event-names";

interface ExtendedSubscriptionData extends subscriptionData {
  licenses: ILicenses[];
}
export interface AccountSubscriptionCardProps {
  subNumber: string;
  renewalDate: string;
  price: string;
  devicesNumber?: number;
  devicesInUse?: number;
  priceCurrency?: string;
  isExpired?: boolean;
  refreshData: () => void;
  cardData: ExtendedSubscriptionData;
}

const AccountSubscriptionCard: React.FC<AccountSubscriptionCardProps> = (props) => {
  const { showToast } = useToast();

  const {
    subNumber,
    // devicesNumber,
    // devicesInUse,
    isExpired,
    cardData,
    refreshData,
  } = props;

  const [downloadToast, setDownloadToast] = useState(false);
  const [isModalUpdateVisible, setIsModalUpdateVisible] = useState(false);
  const [isModalOrderVisible, setIsModalOrderVisible] = useState(false);
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalSuccessfullyVisible, setIsModalSuccessfullyVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [isLicenseModalVisible, setIsLicenseModalVisible] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();


  const downloadLink = t("Download Installer Link");

  const renewalStatus: { text: string; img: string } = useMemo(() => {
    if (isExpired) return { text: "Expired", img: deviceCancel };
    else if (cardData.isCanceled) {
      return { text: "Canceled", img: deviceCancel };
    } else if (cardData.isPaused) {
      return { text: "Paused", img: devicePause };
    } else if (cardData.isActive) {
      return { text: "Active", img: device };
    }
    return { text: "Active", img: device };
  }, [cardData.isActive, cardData.isCanceled, cardData.isPaused, isExpired]);

  // const totalSeats = useMemo(() => {
  //   return Math.max(devicesNumber || 0, devicesInUse || 0, cardData.licenses.length || 0);
  // }, [devicesNumber, devicesInUse, cardData.licenses.length]);

  return (
    <div className={styles.root}>
      <img src={renewalStatus.img} alt="device" height={54} className={styles.root_icon} />
      <div className={styles.root_rightBlock}>
        <div className={styles.root_general}>
          {/* Top */}
          <p>{cardData.skuDescription?.toLocaleUpperCase()}</p>
          <p className={styles.root_general_subscription}>
            {t("Subscription #:")} {subNumber}
          </p>
          {/* End Top */}

          {/* Devices */}
          <div className={styles.root_wrapper}>
            <div className={styles.root_row}>
              {/* <p className={styles.root_general_devices}>{t("Devices in package")}</p>
              <p className={styles.root_general_fraction}>
                {devicesInUse}/{totalSeats}
              </p> */}
            </div>
            {!isExpired && (
              <div>
                <p className={styles.root_general_manage} onClick={() => navigate("/devices")}>
                  {t("Manage Devices")}
                </p>
              </div>
            )}
            {!isExpired && (
              <div>
                <p className={styles.root_general_manage} onClick={() => setIsLicenseModalVisible(true)}>
                  {t("License Keys")}
                </p>
              </div>
            )}
            <div
              className={styles.root_general_installer}
              onClick={() => {
                showToast(t("Install by clicking the download file"));
                window.open(downloadLink, "_blank");
                trackEvent(EVENT_NAMES.INSTALL_DOWNLOADED, {
                  source: "account",
                  sourceScreen: "account",
                });
              }}
            >
              <img src={download} alt="download" width={20} height={20} />
              <p>{t("Download Installer")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* {!trial && (
        <div className={styles.root_general_arrow} onClick={handleArrowClick}>
          <img src={showAllProperties ? arrowUp : arrow} alt="arrow" className={styles.root_general_arrow_icon} />
        </div>
      )} */}

      {downloadToast && (
        <div
          className={styles.root_toast}
          onClick={() => {
            setDownloadToast(false);
          }}
        >
          <img src={install} alt="install" />
          <p>{t("Install by clicking the download file")}</p>
        </div>
      )}

      <Modal isOpen={isModalUpdateVisible} setIsVisible={() => setIsModalUpdateVisible(false)}>
        <GetPremiumModal
          setIsModalUpdateVisible={setIsModalUpdateVisible}
          setIsModalOrderVisible={setIsModalOrderVisible}
        />
      </Modal>

      <Modal isOpen={isModalOrderVisible} setIsVisible={() => setIsModalOrderVisible(false)}>
        <OrderSummaryModal />
      </Modal>

      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfullyVisible}
          data={cardData}
        />
      </Modal>

      <Modal isOpen={isModalProcessingVisible} setIsVisible={() => setIsModalProcessingVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      <Modal isOpen={isModalSuccessfullyVisible} setIsVisible={() => setIsModalSuccessfullyVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfullyVisible(false)}
          refreshData={() => refreshData()}
          data={cardData}
        />
      </Modal>

      <Modal isOpen={isLicenseModalVisible} setIsVisible={() => setIsLicenseModalVisible(false)}>
        <LicenseKeysModal setIsLicenseModalVisible={() => setIsLicenseModalVisible(false)} data={cardData.licenses} />
      </Modal>
    </div>
  );
};

export default AccountSubscriptionCard;
